/**
 * Os nomes curtos de cidade que o povo usa e que só podem ser UMA cidade do
 * estado (tarefa wdwxne035x).
 *
 * Em 21/10/2026 um lead da Braga escreveu "Bragança", e a triagem da entrada o
 * reprovou como fora da área: a área tem "Bragança Paulista/SP", a sede da
 * própria unidade. Ninguém em SP fala "Paulista" no fim; a chave de cidade
 * (`chaveDaCidade.ts`) compara o nome inteiro e não tinha como saber.
 *
 * ─── A regra que gerou a tabela ─────────────────────────────────────────────
 *
 * Tirada da lista oficial dos 5.570 municípios do IBGE (API de localidades,
 * 29/10/2026), com a chave sem acento e em minúsculas. Uma cidade ganha nome
 * curto quando:
 *
 *  1. o nome termina num complemento que o povo larga: "paulista", "potiguar",
 *     "d'oeste", "do oeste", "do sul", "do norte", "do leste", "da serra",
 *     "do campo", "de minas", "de goias" (e os gentílicos dos outros estados,
 *     que hoje não geram nenhuma entrada);
 *  2. o que sobra tem 4 letras ou mais;
 *  3. nenhuma OUTRA cidade do mesmo estado se chama assim nem começa assim.
 *     "Santa Rita d'Oeste" não ganha "Santa Rita" (há "Santa Rita do Passa
 *     Quatro"), "Vargem Grande do Sul" não ganha "Vargem Grande" (há "Vargem
 *     Grande Paulista"), e "Aparecida d'Oeste" não ganha "Aparecida", que é
 *     outra cidade de SP.
 *
 * Por isso "Mogi" não está aqui: é Mogi das Cruzes, Mogi Guaçu ou Mogi Mirim,
 * e o que é ambíguo continua sem casar. Ficar de fora só mantém o que já era.
 *
 * ⚠️ É POR ESTADO. "Bragança" é uma cidade do Pará; o nome curto só vale para
 * "Bragança Paulista" quando o lead não disse o estado ou disse SP. Quem
 * escreve "Bragança/PA" não casa com a área da Braga.
 *
 * Formato: estado → nome curto → o complemento que falta. O nome inteiro é
 * `curto + " " + complemento`. Chaves sem acento, de propósito (é a chave de
 * comparação, como a de `chaveDaCidade.ts`).
 *
 * 🚨 O servidor tem cópia exata em
 * `supabase/functions/_shared/ive/apelidos-de-cidade.ts`; o teste
 * `tests/area/apelidos-de-cidade.test.ts` impede as duas de se separarem.
 */
export const APELIDOS_DE_CIDADE: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  ac: { "cruzeiro": "do sul" },
  al: { "santa luzia": "do norte" },
  am: { "atalaia": "do norte", "nova olinda": "do norte" },
  ba: { "pirai": "do norte" },
  ce: { "antonina": "do norte", "guaraciaba": "do norte", "juazeiro": "do norte", "limoeiro": "do norte",
    "tabuleiro": "do norte" },
  es: { "agua doce": "do norte", "bom jesus": "do norte", "mimoso": "do sul", "rio novo": "do sul", // acentos-ok
    "sao domingos": "do norte" }, // acentos-ok
  go: { "abadia": "de goias", "agua fria": "de goias", "aguas lindas": "de goias", "alto paraiso": "de goias", // acentos-ok
    "alvorada": "do norte", "bela vista": "de goias", "bom jardim": "de goias", "bom jesus": "de goias",
    "campestre": "de goias", "campo alegre": "de goias", "campo limpo": "de goias", "cocalzinho": "de goias",
    "colinas": "do sul", "corumba": "de goias", "divinopolis": "de goias", "estrela": "do norte",
    "flores": "de goias", "gameleira": "de goias", "guarani": "de goias", "ipiranga": "de goias",
    "mimoso": "de goias", "monte alegre": "de goias", "montes claros": "de goias", "morro agudo": "de goias",
    "nova iguacu": "de goias", "ouro verde": "de goias", "palestina": "de goias", "palmeiras": "de goias",
    "petrolina": "de goias", "pilar": "de goias", "santa barbara": "de goias", "santa cruz": "de goias",
    "santa fe": "de goias", "santa helena": "de goias", "santa rosa": "de goias", "santa tereza": "de goias",
    "santa terezinha": "de goias", "sao francisco": "de goias", "sao luiz": "do norte", "taquaral": "de goias", // acentos-ok
    "teresina": "de goias", "terezopolis": "de goias", "valparaiso": "de goias" },
  ma: { "capinzal": "do norte", "miranda": "do norte" },
  mg: { "alvorada": "de minas", "antonio prado": "de minas", "bela vista": "de minas", "bocaina": "de minas",
    "bom jardim": "de minas", "bonfinopolis": "de minas", "bonito": "de minas", "brasilandia": "de minas",
    "brasilia": "de minas", "carmopolis": "de minas", "central": "de minas", "conceicao da barra": "de minas",
    "couto de magalhaes": "de minas", "divinolandia": "de minas", "entre rios": "de minas", "fortaleza": "de minas",
    "fortuna": "de minas", "ibitiura": "de minas", "icarai": "de minas", "imbe": "de minas", "irai": "de minas",
    "itamarati": "de minas", "itau": "de minas", "jenipapo": "de minas", "jose goncalves": "de minas",
    "limeira": "do oeste", "madre de deus": "de minas", "maripa": "de minas", "monte alegre": "de minas",
    "monte santo": "de minas", "morada nova": "de minas", "novo oriente": "de minas", "ouro verde": "de minas",
    "para": "de minas", "patos": "de minas", "rio pardo": "de minas", "rochedo": "de minas",
    "santa efigenia": "de minas", "santa fe": "de minas", "santa helena": "de minas", "santa rosa": "da serra",
    "sao felix": "de minas", "sao roque": "de minas", "sao vicente": "de minas", "serra azul": "de minas", // acentos-ok
    "serranopolis": "de minas", "soledade": "de minas", "taquaracu": "de minas", "uniao": "de minas",
    "uruana": "de minas", "varjao": "de minas" },
  ms: { "chapadao": "do sul", "fatima": "do sul", "nova alvorada": "do sul", "novo horizonte": "do sul",
    "sao gabriel": "do oeste" }, // acentos-ok
  mt: { "boa esperanca": "do norte", "canabrava": "do norte", "conquista": "d'oeste", "figueiropolis": "d'oeste",
    "gaucha": "do norte", "gloria": "d'oeste", "guaranta": "do norte", "ipiranga": "do norte", "lambari": "d'oeste",
    "mirassol": "d'oeste", "nova canaa": "do norte", "novo horizonte": "do norte", "planalto": "da serra",
    "porto alegre": "do norte", "primavera": "do leste", "tangara": "da serra", "terra nova": "do norte",
    "uniao": "do sul" },
  pa: { "agua azul": "do norte", "cumaru": "do norte", "garrafao": "do norte", "ourilandia": "do norte" }, // acentos-ok
  pe: { "sao benedito": "do sul", "taquaritinga": "do norte" }, // acentos-ok
  pr: { "agudos": "do sul", "alvorada": "do sul", "bocaiuva": "do sul", "bom jesus": "do sul",
    "brasilandia": "do sul", "cafezal": "do sul", "campina grande": "do sul", "centenario": "do sul",
    "corumbatai": "do sul", "entre rios": "do oeste", "flor da serra": "do sul", "formosa": "do oeste",
    "iracema": "do oeste", "itapejara": "d'oeste", "itauna": "do sul", "jandaia": "do sul", "jundiai": "do sul",
    "laranjeiras": "do sul", "marilandia": "do sul", "maua": "da serra", "ouro verde": "do oeste",
    "paraiso": "do norte", "pirai": "do sul", "querencia": "do norte", "santa izabel": "do oeste",
    "santa maria": "do oeste", "santa tereza": "do oeste", "sao jeronimo": "da serra", "sao mateus": "do sul", // acentos-ok
    "tijucas": "do sul", "tuneiras": "do oeste", "vera cruz": "do oeste" },
  rj: { "paraiba": "do sul" },
  rn: { "sao francisco": "do oeste", "serra negra": "do norte", "triunfo": "potiguar" }, // acentos-ok
  ro: { "alta floresta": "d'oeste", "alvorada": "d'oeste", "colorado": "do oeste", "espigao": "d'oeste",
    "itapua": "do oeste", "machadinho": "d'oeste", "mirante": "da serra", "nova brasilandia": "d'oeste",
    "novo horizonte": "do oeste", "ouro preto": "do oeste", "pimenteiras": "do oeste", "santa luzia": "d'oeste",
    "sao felipe": "d'oeste" }, // acentos-ok
  rs: { "almirante tamandare": "do sul", "ametista": "do sul", "benjamin constant": "do sul",
    "bom retiro": "do sul", "cacapava": "do sul", "cachoeira": "do sul", "cambara": "do sul",
    "campestre": "da serra", "campinas": "do sul", "capao bonito": "do sul", "capivari": "do sul",
    "caxias": "do sul", "coqueiros": "do sul", "cruzeiro": "do sul", "eldorado": "do sul", "encruzilhada": "do sul",
    "entre rios": "do sul", "esperanca": "do sul", "ipiranga": "do sul", "itatiba": "do sul",
    "lagoa bonita": "do sul", "lavras": "do sul", "monte belo": "do sul", "morrinhos": "do sul",
    "nova esperanca": "do sul", "nova roma": "do sul", "palmares": "do sul", "paraiso": "do sul",
    "rosario": "do sul", "santa barbara": "do sul", "santa cecilia": "do sul", "santa clara": "do sul",
    "santa cruz": "do sul", "santa margarida": "do sul", "santo expedito": "do sul", "sao domingos": "do sul", // acentos-ok
    "sao lourenco": "do sul", "sao valerio": "do sul", "sao vicente": "do sul", "sapucaia": "do sul", // acentos-ok
    "sentinela": "do sul", "taquarucu": "do sul", "tiradentes": "do sul", "trindade": "do sul", "tupanci": "do sul",
    "uniao": "da serra", "vila nova": "do sul" },
  sc: { "balneario barra": "do sul", "bocaina": "do sul", "bom jardim": "da serra", "campo belo": "do sul",
    "caxambu": "do sul", "cocal": "do sul", "formosa": "do sul", "guaruja": "do sul", "herval": "d'oeste",
    "ipora": "do oeste", "jaragua": "do sul", "lindoia": "do sul", "santiago": "do sul", "sao bento": "do sul", // acentos-ok
    "sao cristovao": "do sul", "sao francisco": "do sul", "sao lourenco": "do oeste", "timbe": "do sul", // acentos-ok
    "uniao": "do oeste" },
  sp: { "aracoiaba": "da serra", "boa esperanca": "do sul", "braganca": "paulista", "cabralia": "paulista",
    "cachoeira": "paulista", "campo limpo": "paulista", "campos novos": "paulista", "cristais": "paulista",
    "euclides da cunha": "paulista", "florida": "paulista", "guarani": "d'oeste", "inubia": "paulista",
    "itapecerica": "da serra", "itapirapua": "paulista", "laranjal": "paulista", "lencois": "paulista",
    "maraba": "paulista", "monte alegre": "do sul", "monte azul": "paulista", "murutinga": "do sul",
    "natividade": "da serra", "nazare": "paulista", "neves": "paulista", "nova canaa": "paulista",
    "palmares": "paulista", "palmeira": "d'oeste", "paraguacu": "paulista", "patrocinio": "paulista",
    "pedrinhas": "paulista", "pilar": "do sul", "redencao": "da serra", "rio grande": "da serra",
    "santa barbara": "d'oeste", "santa clara": "d'oeste", "santa fe": "do sul", "santa maria": "da serra",
    "sao bernardo": "do campo", "sao caetano": "do sul", "sao lourenco": "da serra", "sebastianopolis": "do sul", // acentos-ok
    "taboao": "da serra", "tupi": "paulista", "uniao": "paulista", "varzea": "paulista" },
}
